import { useMemo, useState } from 'react'
import {
  CENARIOS_EXTRACAO,
  ETAPAS_DECISAO_EXTRACAO,
  ETAPAS_XABCDE,
  type EtapaDecisaoExtracao,
  type EtapaXabcde,
  type ModoExperienciaExtracao,
} from '../dominio/didatico/cenariosExtracao'
import {
  achadosRevelados,
  aprisionamentosVisiveis,
  gerarDebriefing,
  vitimaSeveridadeVisivel,
  type PrioridadeDeclarada,
  type ProntidaoExtracao,
  type SessaoExtracao,
} from '../dominio/didatico/sessaoExtracao'
import { compararComReferencia, type ReferenciaCorrecaoVitima } from '../dominio/didatico/cenariosAutorais'

export interface ComparacaoGeometriaExtracao {
  rotulo: string
  antes: string
  depois: string
  efeito: string
}

interface Props {
  sessao: SessaoExtracao
  comparacaoGeometria: readonly ComparacaoGeometriaExtracao[]
  estrategia: {
    abertura: string
    giroGraus: number
    folgaCm: number
    obstaculos: number
    interferenciaVitima: boolean
  }
  aoEscolherModo: (modo: ModoExperienciaExtracao) => void
  aoEscolherCenario: (id: string) => void
  aoNavegar: (etapa: EtapaDecisaoExtracao) => void
  aoFocarVitima: (id: string) => void
  aoAvaliar: (etapa: EtapaXabcde) => void
  aoJustificar: (etapa: EtapaDecisaoExtracao, texto: string) => void
  aoPreparar: (alteracao: Partial<ProntidaoExtracao>, descricao: string) => void
  aoDecidirMomento: (retirarAgora: boolean) => void
  aoEscolherOrdem: (vitimaId: string, justificativa?: string) => void
  /** "Enquadrar esta ação": leva a câmera para a vítima e a prancha, sem mudar a decisão nem a técnica. */
  aoEnquadrarAcao?: () => void
  /** Fase 5B — cenários autorais do professor, para o seletor de "Cenário pronto" */
  cenariosAutorais?: readonly { id: string; nome: string }[]
  aoDeclararClassificacao: (vitimaId: string, prioridade: PrioridadeDeclarada, justificativa: string) => void
  aoDeclararAtencaoInicial: (vitimaId: string, justificativa: string) => void
  aoLiberarDebriefing: () => void
  /** presente só quando o cenário atual é autoral e tem referência de correção */
  referenciaCorrecao?: readonly ReferenciaCorrecaoVitima[]
}

const NOMES_PRIORIDADE: Record<PrioridadeDeclarada, string> = {
  alta: 'Alta',
  moderada: 'Moderada',
  baixa: 'Baixa',
  dadosInsuficientes: 'Dados insuficientes — preciso avaliar mais',
}

const TITULOS_ETAPA: Record<EtapaDecisaoExtracao, string> = {
  porque: '1 ANALISAR E DECIDIR',
  quando: '2 QUANDO?',
  como: '3 COMO?',
  debriefing: '4 DEBRIEFING',
}

const rotuloProntidao = (valor: boolean | string) => {
  if (valor === true || valor === 'sim' || valor === 'concluido') return 'sim'
  if (valor === false || valor === 'nao' || valor === 'pendente') return 'não'
  return valor
}

export function PainelDecisaoExtracao(props: Props) {
  const { sessao } = props
  const [justificativa, setJustificativa] = useState('')
  const [prioridadeEscolhida, setPrioridadeEscolhida] = useState<PrioridadeDeclarada>('moderada')
  const [justificativaClassificacao, setJustificativaClassificacao] = useState('')
  const [justificativaAtencao, setJustificativaAtencao] = useState('')
  const vitima = sessao.cenario.vitimas.find((item) => item.id === sessao.vitimaFocoId) ?? sessao.cenario.vitimas[0]!
  const avaliadas = new Set(sessao.avaliacoes[vitima.id]?.etapasAvaliadas ?? [])
  const revelados = achadosRevelados(sessao, vitima.id)
  const aprisionamentos = aprisionamentosVisiveis(sessao, vitima.id)
  const debriefing = useMemo(() => gerarDebriefing(sessao), [sessao])
  const classificacaoAtual = sessao.classificacoesAluno[vitima.id]
  const comparacaoReferencia = useMemo(
    () => (props.referenciaCorrecao ? compararComReferencia(sessao, props.referenciaCorrecao) : []),
    [sessao, props.referenciaCorrecao],
  )

  const registrarTexto = (etapa: EtapaDecisaoExtracao) => {
    props.aoJustificar(etapa, justificativa)
    setJustificativa('')
  }

  return (
    <div className="painel-decisao-extracao">
      <section className="grupo-controle seletor-modo-extracao" aria-label="Modo da extração">
        <span className="titulo-controle">Modo de ensino</span>
        <div className="cartoes-modo-extracao">
          <button className={sessao.modo === 'demonstracao' ? 'ativo' : ''} onClick={() => props.aoEscolherModo('demonstracao')}>
            <strong>Demonstração de retirada</strong>
            <small>Liberdade direta para mostrar como executar.</small>
          </button>
          <button className={sessao.modo === 'cenario' ? 'ativo' : ''} onClick={() => props.aoEscolherModo('cenario')}>
            <strong>Simulado de decisão · cenário com vítima</strong>
            <small>Analise o caso, decida a sequência e justifique.</small>
          </button>
        </div>
      </section>

      {sessao.modo === 'demonstracao' ? (
        <section className="grupo-controle modo-demonstracao-resumo">
          <span className="titulo-controle">Modo professor · COMO retirar?</span>
          <p className="dica">
            Não há protocolo de triagem obrigatório. Use diretamente Vítimas, Posição, Rota, Técnica,
            Procedimentos e Reprodução para montar a demonstração.
          </p>
          <p className="destaque-decisao">A câmera continua livre durante toda a animação.</p>
        </section>
      ) : (
        <>
          <section className="grupo-controle">
            <label className="campo">
              <span>Cenário pronto</span>
              <select value={sessao.cenario.id} onChange={(evento) => props.aoEscolherCenario(evento.target.value)}>
                <optgroup label="Catálogo">
                  {CENARIOS_EXTRACAO.map((cenario) => <option key={cenario.id} value={cenario.id}>{cenario.nome}</option>)}
                </optgroup>
                {props.cenariosAutorais && props.cenariosAutorais.length > 0 && (() => {
                  const slides = props.cenariosAutorais.filter((c) => c.id.startsWith('slide-'))
                  const outros = props.cenariosAutorais.filter((c) => !c.id.startsWith('slide-'))
                  return (
                    <>
                      {slides.length > 0 && (
                        <optgroup label="Cenários dos slides de aula">
                          {slides.map((cenario) => (
                            <option key={cenario.id} value={cenario.id}>
                              {cenario.nome}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {outros.length > 0 && (
                        <optgroup label="Cenários autorais">
                          {outros.map((cenario) => (
                            <option key={cenario.id} value={cenario.id}>
                              {cenario.nome}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  )
                })()}
              </select>
            </label>
            <p className="dica">{sessao.cenario.resumo}</p>
            <p className="objetivo-cenario"><strong>Objetivo:</strong> {sessao.cenario.objetivo}</p>
          </section>

          <nav className="etapas-decisao-extracao" aria-label="Etapas da decisão">
            {ETAPAS_DECISAO_EXTRACAO.map((etapa) => (
              <button key={etapa} className={sessao.etapa === etapa ? 'ativo' : ''} onClick={() => props.aoNavegar(etapa)}>
                {TITULOS_ETAPA[etapa]}
              </button>
            ))}
          </nav>

          {sessao.etapa === 'porque' && (
            <section className="grupo-controle etapa-pedagogica">
              <span className="titulo-controle">POR QUÊ? · Quem é meu paciente e qual é o problema?</span>
              {sessao.cenario.vitimas.length > 1 && <h4>Triagem inicial</h4>}
              <div className="cartoes-triagem">
                {sessao.cenario.vitimas.map((item) => (
                  <button key={item.id} className={item.id === vitima.id ? 'ativo' : ''} onClick={() => props.aoFocarVitima(item.id)}>
                    <strong>{item.nome}</strong>
                    <small>{sessao.cenario.veiculos.find((veiculo) => veiculo.id === item.veiculoId)?.nome}</small>
                    <ul>{item.perceptivel.map((texto) => <li key={texto}>{texto}</li>)}</ul>
                  </button>
                ))}
              </div>
              {sessao.cenario.vitimas.length > 1 && <p className="pergunta-decisao">Qual vítima deve receber avaliação/intervenção primeiro?</p>}

              <p className="dica">Use os achados visíveis do caso para discutir prioridade, acesso, aprisionamento e riscos. O sistema não faz classificação automática.</p>

              {sessao.cenario.vitimas.length > 1 && (
                <div className="atencao-inicial-extracao">
                  <span className="titulo-controle">Quem recebe atenção/intervenção primeiro?</span>
                  <p className="dica">Esta decisão pode ser diferente da ordem física de retirada.</p>
                  <div className="linha-botoes">
                    {sessao.cenario.vitimas.map((item) => (
                      <button
                        key={item.id}
                        className={sessao.atencaoInicial.vitimaId === item.id ? 'ativo' : ''}
                        onClick={() => props.aoDeclararAtencaoInicial(item.id, justificativaAtencao)}
                      >
                        {item.nome}
                      </button>
                    ))}
                  </div>
                  <label className="campo campo-justificativa">
                    <span>Por quê?</span>
                    <textarea
                      value={justificativaAtencao}
                      onChange={(evento) => setJustificativaAtencao(evento.target.value)}
                      placeholder="Justifique a prioridade de atenção."
                    />
                  </label>
                </div>
              )}

              <label className="campo campo-justificativa">
                <span>Justificativa da estratégia</span>
                <textarea value={justificativa} onChange={(evento) => setJustificativa(evento.target.value)} placeholder="Digite uma justificativa curta (opcional)." />
              </label>
              <button onClick={() => registrarTexto('porque')} disabled={!justificativa.trim()}>Registrar justificativa</button>
            </section>
          )}

          {sessao.etapa === 'quando' && (
            <section className="grupo-controle etapa-pedagogica">
              <span className="titulo-controle">QUANDO? · O cenário e o paciente estão prontos?</span>
              {sessao.cenario.pausas[0] && <p className="destaque-decisao">Pausa didática: {sessao.cenario.pausas[0]}</p>}
              <dl className="prontidao-extracao">
                <div><dt>Veículo estabilizado</dt><dd>{rotuloProntidao(sessao.prontidao.veiculoEstabilizado)}</dd></div>
                <div><dt>Riscos gerenciados</dt><dd>{rotuloProntidao(sessao.prontidao.riscosGerenciados)}</dd></div>
                <div><dt>Acesso à vítima</dt><dd>{rotuloProntidao(sessao.prontidao.acessoVitima)}</dd></div>
                <div><dt>Procedimentos internos</dt><dd>{rotuloProntidao(sessao.prontidao.procedimentosInternos)}</dd></div>
                <div><dt>Rota disponível</dt><dd>{rotuloProntidao(sessao.prontidao.rotaDisponivel)}</dd></div>
                <div><dt>Paciente pronto para movimentação</dt><dd>{rotuloProntidao(sessao.prontidao.pacientePronto)}</dd></div>
              </dl>
              <div className="acoes-preparacao">
                <button onClick={() => props.aoPreparar({ veiculoEstabilizado: true }, 'Veículo estabilizado antes da retirada.')}>Confirmar estabilização</button>
                <button onClick={() => props.aoPreparar({ riscosGerenciados: true }, 'Riscos identificados e gerenciados.')}>Gerenciar riscos</button>
                <button onClick={() => props.aoPreparar({ acessoVitima: true }, 'Acesso à vítima criado.')}>Criar acesso</button>
                <button onClick={() => props.aoPreparar({ pacientePronto: 'sim' }, 'Paciente avaliado como pronto para movimentação.')}>Paciente pronto</button>
              </div>
              {props.comparacaoGeometria.length > 0 && (
                <div className="comparacao-antes-depois">
                  <h4>Antes / depois dos procedimentos</h4>
                  {props.comparacaoGeometria.map((item) => (
                    <article key={item.rotulo}>
                      <strong>{item.rotulo}</strong>
                      <span>Antes: {item.antes}</span>
                      <span>Depois: {item.depois}</span>
                      <small>{item.efeito}</small>
                    </article>
                  ))}
                </div>
              )}
              <p className="pergunta-decisao">É o momento de retirar?</p>
              <div className="linha-botoes">
                <button className="primario" onClick={() => props.aoDecidirMomento(true)}>Retirar agora</button>
                <button onClick={() => props.aoDecidirMomento(false)}>Preparar mais</button>
              </div>
              <p className="dica">A tentativa não é bloqueada: pendências e consequências aparecem no debriefing.</p>
            </section>
          )}

          {sessao.etapa === 'como' && (
            <section className="grupo-controle etapa-pedagogica">
              <span className="titulo-controle">COMO? · Qual técnica e trajetória atendem melhor?</span>
              {sessao.cenario.pausas[1] && <p className="destaque-decisao">Pausa didática: {sessao.cenario.pausas[1]}</p>}
              <div className="resumo-estrategia">
                <strong>{props.estrategia.abertura}</strong>
                <span>Giro: {props.estrategia.giroGraus.toFixed(0)}°</span>
                <span>Folga mínima: {props.estrategia.folgaCm.toFixed(0)} cm</span>
                <span>Obstáculos relevantes: {props.estrategia.obstaculos}</span>
                {props.estrategia.interferenciaVitima && <span>Outra vítima interfere nesta trajetória.</span>}
              </div>
              {props.aoEnquadrarAcao && (
                <button type="button" onClick={props.aoEnquadrarAcao}>
                  Enquadrar esta ação
                </button>
              )}
              {sessao.cenario.vitimas.length > 1 && (
                <>
                  <p className="pergunta-decisao">Qual vítima você retiraria primeiro?</p>
                  <div className="linha-botoes">
                    {sessao.cenario.vitimas.map((item) => (
                      <button key={item.id} disabled={sessao.ordemRetirada.includes(item.id)} onClick={() => props.aoEscolherOrdem(item.id, justificativa)}>
                        {item.nome}
                      </button>
                    ))}
                  </div>
                </>
              )}
              <label className="campo campo-justificativa">
                <span>Justifique a técnica, a abertura ou a ordem escolhida</span>
                <textarea value={justificativa} onChange={(evento) => setJustificativa(evento.target.value)} placeholder="A escolha pode ser defensável por diferentes trade-offs." />
              </label>
              <button onClick={() => registrarTexto('como')} disabled={!justificativa.trim()}>Registrar justificativa</button>
              <p className="dica">Use os grupos Rota, Obstáculos, Teto e técnica e Reprodução para executar com os cálculos 3D existentes.</p>
            </section>
          )}

          {sessao.etapa === 'debriefing' && (
            <section className="grupo-controle etapa-pedagogica">
              <span className="titulo-controle">Debriefing · decisões, não apenas nota</span>
              <ol className="linha-tempo-debriefing">
                {debriefing.linhaDoTempo.map((item) => (
                  <li key={item.id}>
                    <strong>{item.ordem}. {item.titulo}</strong>
                    <span>{item.descricao}</span>
                    {item.justificativa && <em>“{item.justificativa}”</em>}
                    {item.consequencias?.map((texto) => <small key={texto}>{texto}</small>)}
                  </li>
                ))}
              </ol>
              <DebriefBloco titulo="O que favoreceu sua estratégia" itens={debriefing.favoreceu} />
              <DebriefBloco titulo="O que dificultou" itens={debriefing.dificultou} />
              <DebriefBloco titulo="Outras alternativas possíveis" itens={debriefing.alternativas} />
              <DebriefBloco titulo="Pontos para discussão" itens={debriefing.discussao} />

              {props.referenciaCorrecao && props.referenciaCorrecao.length > 0 && (
                <div className="referencia-professor-debriefing">
                  <span className="titulo-controle">Referência do professor</span>
                  {!sessao.debriefingLiberado ? (
                    <>
                      <p className="dica">A referência do professor ainda não foi liberada nesta sessão.</p>
                      <button onClick={props.aoLiberarDebriefing}>Liberar referência do professor (ação do instrutor)</button>
                    </>
                  ) : (
                    comparacaoReferencia.map((item) => {
                      const vitimaComparada = sessao.cenario.vitimas.find((v) => v.id === item.vitimaId)
                      return (
                        <article key={item.vitimaId}>
                          <strong>{vitimaComparada?.nome ?? item.vitimaId}</strong>
                          <span>
                            Achados essenciais ainda não encontrados: {item.achadosEssenciaisFaltando.join(', ') || 'nenhum'}
                          </span>
                          <span>
                            Sua classificação: {item.classificacaoAluno ? NOMES_PRIORIDADE[item.classificacaoAluno.prioridade] : 'não declarada'}
                          </span>
                          <span>Classificação esperada (professor): {item.referencia.classificacaoEsperada || 'não preenchida'}</span>
                          {item.referencia.alternativasAceitas.length > 0 && (
                            <small>Alternativas também aceitas: {item.referencia.alternativasAceitas.join('; ')}</small>
                          )}
                          {item.referencia.prioridadesAtencao && <small>Prioridades de atenção: {item.referencia.prioridadesAtencao}</small>}
                          {item.referencia.riscosDeAtraso && <small>Riscos de atraso: {item.referencia.riscosDeAtraso}</small>}
                          {item.referencia.restricoesGeometricas && <small>Restrições geométricas: {item.referencia.restricoesGeometricas}</small>}
                          {item.referencia.pontosDeJulgamento && <small>Depende do julgamento do instrutor: {item.referencia.pontosDeJulgamento}</small>}
                        </article>
                      )
                    })
                  )}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  )
}

function DebriefBloco({ titulo, itens }: { titulo: string; itens: readonly string[] }) {
  return <div className="bloco-debriefing"><h4>{titulo}</h4><ul>{itens.map((item) => <li key={item}>{item}</li>)}</ul></div>
}
